import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import axios from 'axios';
import { API_URL } from '../../config';
import { normalizePath } from '../lib/seo';

// Mirrors the backend SeoSetting defaults so the site renders sensible meta
// even when the config endpoint is unreachable (or on older deployments).
export const FALLBACK_SETTINGS = {
    siteName: 'The Discerner',
    siteUrl: 'https://discerner.blog',
    titleTemplate: '%s - The Discerner',
    defaultTitle: 'The Discerner - Laymen on Agathology & Ponerology',
    defaultDescription: 'Orthodox Christian laymen writing on agathology and ponerology — the study of good and evil as they manifest in culture, power, media, and sacred history.',
    defaultKeywords: [],
    defaultSocialImage: '',
    twitterHandle: '',
    googleSiteVerification: '',
    bingSiteVerification: '',
    robotsExtra: '',
};

const SeoContext = createContext(null);

export function SeoProvider({ children }) {
    const [settings, setSettings] = useState(FALLBACK_SETTINGS);
    const [pages, setPages] = useState([]);
    const [redirects, setRedirects] = useState([]);
    const [status, setStatus] = useState('loading');

    const load = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/api/seo/config`);
            const loadedSettings = response.data?.settings;
            const loadedPages = response.data?.pages;
            // SPA catch-all hosts answer missing API routes with 200 + HTML;
            // guard on shape before trusting the payload.
            if (!loadedSettings || typeof loadedSettings !== 'object') {
                throw new Error('seo config endpoint unavailable');
            }
            // Identity fields the site can't render without: a blanked value
            // (admin cleared the input) falls back instead of propagating ''.
            const merged = { ...FALLBACK_SETTINGS, ...loadedSettings };
            ['siteName', 'siteUrl', 'titleTemplate', 'defaultTitle', 'defaultDescription'].forEach((field) => {
                if (!merged[field]) {
                    merged[field] = FALLBACK_SETTINGS[field];
                }
            });
            setSettings(merged);
            setPages(Array.isArray(loadedPages) ? loadedPages : []);
            setRedirects(Array.isArray(response.data?.redirects) ? response.data.redirects : []);
            setStatus('ready');
        } catch (error) {
            console.error('[ERROR] Failed to load SEO config:', error);
            setStatus('error');
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const value = useMemo(() => {
        const overridesByPath = new Map();
        pages.forEach((page) => {
            const path = normalizePath(page?.path);
            if (path) {
                overridesByPath.set(path, page);
            }
        });

        const redirectsByPath = new Map();
        redirects.forEach((entry) => {
            const from = normalizePath(entry?.fromPath);
            if (from && entry?.toPath) {
                redirectsByPath.set(from, entry.toPath);
            }
        });

        return {
            status,
            settings,
            pages,
            redirects,
            getOverrideForPath: (path) => overridesByPath.get(normalizePath(path)) || null,
            getRedirectForPath: (path) => redirectsByPath.get(normalizePath(path)) || null,
            refresh: load,
        };
    }, [status, settings, pages, redirects, load]);

    return (
        <SeoContext.Provider value={value}>
            {children}
        </SeoContext.Provider>
    );
}

export const useSeoConfig = () => {
    const context = useContext(SeoContext);
    if (!context) {
        throw new Error('useSeoConfig must be used within a SeoProvider');
    }
    return context;
};
